import type { StateTree } from 'pinia'

const isStateTree = (state: unknown): state is StateTree => {
  return typeof state === 'object' && state !== null
}

export default defineNuxtPlugin({
  name: 'sentry',
  dependsOn: ['pinia', 'unleash'],
  async setup(nuxtApp) {
    const {
      public: { sentry, domainName, mode, isTest },
    } = useRuntimeConfig()

    const isDisabled = mode === 'development' || isTest

    if (!sentry.dsn || isDisabled) {
      return {}
    }

    const router = useRouter()
    const { $pinia, $unleash } = useNuxtApp()

    const Sentry = await import('@sentry/vue')

    Sentry.init({
      app: nuxtApp.vueApp,
      dsn: sentry.dsn,
      environment: sentry.config.environment,
      release: sentry.config.release,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
        Sentry.unleashIntegration({ featureFlagClientClass: $unleash.baseType }),
      ],
      tracePropagationTargets: ['localhost', `https://my.${domainName}`],
      tracesSampleRate: mode === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: mode === 'production' ? 0.01 : 1.0,
      replaysOnErrorSampleRate: 1.0,
    })

    $pinia.use(
      Sentry.createSentryPiniaPlugin({
        stateTransformer(state) {
          // We only want to send selected parts of the state
          const transformedState: Record<string, StateTree> = {}

          if (isStateTree(state.user)) {
            const { role } = state.user
            transformedState.user = { role }
          }

          if (isStateTree(state.business)) {
            const { id, pricePlan } = state.business
            transformedState.business = { id, pricePlan }
          }

          return transformedState
        },
      })
    )

    if ('LEGACY' in import.meta.env) {
      Sentry.setTag('build_type', import.meta.env.LEGACY ? 'legacy' : 'modern')
    }

    return {
      provide: {
        sentry: {
          captureException: Sentry.captureException,
          captureMessage: Sentry.captureMessage,
          captureEvent: Sentry.captureEvent,
          setUser: Sentry.setUser,
          setTag: Sentry.setTag,
        },
      },
    }
  },
})
